// Time clock — the wire between a punch and AppSync.
//
// Inline documents on purpose: codegen has not run since TimeEntry was added to
// the schema, so queries.js/mutations.js don't know the model exists. Same
// reason roadStatusEvents.js carries its own.
//
// Every call here degrades instead of throwing, and `available` reports whether
// the model is actually deployed on this backend. TimeEntry is additive — a
// backend that hasn't taken the schema push yet must still open a job page,
// with the clock hidden rather than exploding.

import { generateClient } from 'aws-amplify/api';
import { TIME_ENTRY_KINDS, nowSeconds, entrySeconds } from '../engine/timeClock';

const client = generateClient();

const ENTRY_FIELDS = `
  id locationId workOrderId roadServiceCallId
  technicianId techNumber techName
  kind startedAt endedAt seconds
  billedAt billedEstimateId note source
`;

const ENTRIES_BY_WORK_ORDER = `
  query TimeEntriesByWorkOrder($workOrderId: ID!) {
    timeEntriesByWorkOrder(workOrderId: $workOrderId, sortDirection: ASC, limit: 500) {
      items { ${ENTRY_FIELDS} }
    }
  }
`;

const CREATE_TIME_ENTRY = `
  mutation CreateTimeEntry($input: CreateTimeEntryInput!) {
    createTimeEntry(input: $input) { ${ENTRY_FIELDS} }
  }
`;

const UPDATE_TIME_ENTRY = `
  mutation UpdateTimeEntry($input: UpdateTimeEntryInput!) {
    updateTimeEntry(input: $input) { ${ENTRY_FIELDS} }
  }
`;

export const TIME_CLOCK_SOURCE = 'web';

// A schema error means the model isn't deployed; a network blip does not. Only
// the first should hide the clock — the second should leave it alone and retry.
function isSchemaError(err) {
  const msgs = [err?.message, ...(err?.errors || []).map(e => e?.message)]
    .filter(Boolean).join(' ');
  return /Cannot query field|Unknown type|Validation error|Unknown argument|doesn't exist/i.test(msgs);
}

/**
 * Every entry on a job, oldest first.
 * Returns { items, available } — `available: false` only when the backend has
 * no TimeEntry model.
 */
export async function listEntriesForWorkOrder(workOrderId) {
  if (!workOrderId) return { items: [], available: true };
  try {
    const result = await client.graphql({
      query: ENTRIES_BY_WORK_ORDER,
      variables: { workOrderId },
    });
    return { items: result.data?.timeEntriesByWorkOrder?.items || [], available: true };
  } catch (err) {
    if (isSchemaError(err)) return { items: [], available: false };
    console.warn('time entry fetch failed:', err?.errors?.[0]?.message || err.message);
    return { items: [], available: true };
  }
}

/** Start a shop clock. Returns the created entry, or null if the write failed. */
export async function startShopClock({ workOrderId, locationId, technician, note = null }) {
  const input = {
    locationId,
    workOrderId,
    technicianId: technician?.id || null,
    techNumber:   technician?.number || null,
    techName:     technician?.name || null,
    kind:         TIME_ENTRY_KINDS.SHOP,
    startedAt:    nowSeconds(),
    note,
    source:       TIME_CLOCK_SOURCE,
  };
  try {
    const result = await client.graphql({ query: CREATE_TIME_ENTRY, variables: { input } });
    return result.data?.createTimeEntry || null;
  } catch (err) {
    console.warn('clock in failed:', err?.errors?.[0]?.message || err.message);
    return null;
  }
}

/**
 * Stop a running entry. `seconds` is written at stop time so the duration is a
 * stored fact, not something re-derived from two timestamps on every read.
 */
export async function stopClock(entry) {
  if (!entry?.id) return null;
  const endedAt = nowSeconds();
  const input = {
    id: entry.id,
    endedAt,
    seconds: entrySeconds(entry, endedAt),
  };
  try {
    const result = await client.graphql({ query: UPDATE_TIME_ENTRY, variables: { input } });
    return result.data?.updateTimeEntry || null;
  } catch (err) {
    console.warn('clock out failed:', err?.errors?.[0]?.message || err.message);
    return null;
  }
}

/**
 * Stamp entries as billed once their hours are on an estimate, so the same hour
 * can't be billed twice. Best-effort per row: one failure doesn't strand the
 * rest, and an unstamped row simply shows up as unbilled again.
 */
export async function markEntriesBilled(entries, estimateId) {
  const billedAt = nowSeconds();
  const results = await Promise.all((entries || []).map(async (entry) => {
    try {
      const result = await client.graphql({
        query: UPDATE_TIME_ENTRY,
        variables: { input: { id: entry.id, billedAt, billedEstimateId: estimateId || null } },
      });
      return result.data?.updateTimeEntry || null;
    } catch (err) {
      console.warn('mark billed failed:', err?.errors?.[0]?.message || err.message);
      return null;
    }
  }));
  return results.filter(Boolean);
}
